import type { ReactNode } from "react";

import { usePermissions } from "@/common/hooks/usePermissions";
import type {
  PermissionAction,
  PermissionResource,
} from "@/common/hooks/usePermissions";

interface PermissionGateProps {
  /** RBAC resource — must be one of the defined PermissionResource values */
  resource: PermissionResource;
  /** RBAC action — must be one of: READ | WRITE | DELETE | EXECUTE | EXPORT */
  action: PermissionAction;
  /** Content to show when the user has permission */
  children: ReactNode;
  /** Optional fallback when the user lacks permission (defaults to null) */
  fallback?: ReactNode;
  /**
   * Optional additional client-side condition (e.g. a record-ownership check
   * like useCanDeleteTestCase) that must also be true, on top of the base
   * role permission, for `children` to render. Defaults to true (no extra
   * restriction). Use this to combine role-level RBAC with per-record rules
   * such as "Testers can only edit test cases they created".
   */
  extraCondition?: boolean;
}

/**
 * Renders `children` only when the current user has the required permission.
 * Falls back to `fallback` (default: nothing) when access is denied.
 *
 * Usage:
 *   <PermissionGate resource="TEST_CASES" action="WRITE">
 *     <Button>Create Test Case</Button>
 *   </PermissionGate>
 */
export function PermissionGate({
  resource,
  action,
  children,
  fallback = null,
  extraCondition = true,
}: PermissionGateProps) {
  const { can } = usePermissions();

  if (!can(resource, action) || !extraCondition) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
